import React, {useEffect, useMemo, useRef, useState} from 'react';
import Toggler from "../../../elements/Toggler";
import SelectNested from "../../../elements/SelectNested";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowsRotate, faTrashAlt} from "@fortawesome/free-solid-svg-icons";
import ElementRemovalWarningModal from "../../common/ElementRemovalWarningModal";
import EmployeePositionRatesModal from "./EmployeePositionRatesModal";

const RATE_SOURCE = {
  CONTRACT: "CONTRACT",
  POSITION: "POSITION",
  INDIVIDUAL: "INDIVIDUAL"
};

const BILLING_TYPE = {
  HOURLY: "HOURLY",
  MONTHLY: "MONTHLY"
};

const money = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numeric);
};

const positiveRate = (value) => {
  if (value === null || value === undefined || value === "") return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0;
};

const EmployeePosition = ({ employeePosition, index, state, onChange, positionsList, onRemove, settings }) => {
  const employee = state.currentItem;
  const [showModal, setShowModal] = useState(false);
  const [missingTrainingsNote, setMissingTrainingsNote] = useState('');
  const [missingCertificationsNote, setMissingCertificationsNote] = useState('');
  const [showRatesModal, setShowRatesModal] = useState(false);
  const previousPositionId = useRef(employeePosition.position?.id ?? null);

  const evaluationDate = employeePosition.evaluationDate || new Date().toISOString().split("T")[0];
  const evaluation = employeePosition.evaluation || settings.positionEvaluationDefaultValue;
  const currentContract = employee?.latestContract || null;
  const position = employeePosition.position || null;

  const contractAvailability = useMemo(() => {
    if (!currentContract?.id) return {available: false, reason: "brak obowiązującej umowy"};

    const today = new Date().toISOString().split("T")[0];
    const validFrom = currentContract.dateFrom;
    const validTo = currentContract.dateTo;
    const activeByDate = (!validFrom || validFrom <= today) && (!validTo || validTo >= today);
    const activeByStatus = currentContract.isValid !== "N";
    const billingType = String(currentContract.billingType || "").toUpperCase();
    const grossRate = billingType === BILLING_TYPE.MONTHLY
      ? currentContract.monthlyRate
      : currentContract.hourlyRate;

    if (!activeByDate || !activeByStatus) {
      return {available: false, reason: "brak obowiązującej umowy"};
    }
    if (![BILLING_TYPE.HOURLY, BILLING_TYPE.MONTHLY].includes(billingType) || !positiveRate(grossRate)) {
      return {available: false, reason: "umowa nie ma zdefiniowanej stawki"};
    }

    return {
      available: true,
      billingType,
      grossRate,
      netRate: billingType === BILLING_TYPE.MONTHLY ? currentContract.netMonthlyRate : currentContract.netHourlyRate,
      label: `${currentContract.identifier || "Umowa"}: ${money(grossRate)} zł/${billingType === BILLING_TYPE.MONTHLY ? "mies." : "h"}`
    };
  }, [currentContract]);

  const positionAvailability = useMemo(() => {
    if (!position?.id || !positiveRate(position.basicWage)) {
      return {available: false, reason: "stanowisko nie ma zdefiniowanej stawki"};
    }
    return {
      available: true,
      billingType: BILLING_TYPE.HOURLY,
      grossRate: position.basicWage,
      netRate: position.basicNetWage,
      label: `${position.name || "Stanowisko"}: ${money(position.basicWage)} zł/h`
    };
  }, [position]);

  const hasStoredRate = positiveRate(employeePosition.hourlyRate) || positiveRate(employeePosition.monthlyRate);
  const selectedRateSource = employeePosition.rateSource || (hasStoredRate ? RATE_SOURCE.INDIVIDUAL : "");
  const billingType = employeePosition.rateBillingType
    || (positiveRate(employeePosition.monthlyRate) ? BILLING_TYPE.MONTHLY : BILLING_TYPE.HOURLY);
  const legacyRate = !employeePosition.rateSource && hasStoredRate;

  const storedGrossRate = billingType === BILLING_TYPE.MONTHLY
    ? employeePosition.monthlyRate
    : employeePosition.hourlyRate;
  const storedNetRate = billingType === BILLING_TYPE.MONTHLY
    ? employeePosition.netMonthlyRate
    : employeePosition.netHourlyRate;

  const sourceDescription = selectedRateSource === RATE_SOURCE.CONTRACT
    ? "Stawka została skopiowana z umowy i zapisana przy pracowniku. Późniejsza zmiana umowy nie zmieni jej automatycznie."
    : selectedRateSource === RATE_SOURCE.POSITION
      ? "Stawka została skopiowana z definicji stanowiska i zapisana przy pracowniku. Późniejsza zmiana stawki stanowiska nie zmieni jej automatycznie."
      : selectedRateSource === RATE_SOURCE.INDIVIDUAL
        ? legacyRate
          ? "Dotychczasowa stawka jest traktowana jako indywidualna. Zapisanie pracownika utrwali nowy sposób oznaczania źródła."
          : "Stawka jest zapisana bezpośrednio przy pracowniku i nie zależy od umowy ani od stawki globalnej stanowiska."
        : "Wybierz źródło stawki. Po wyborze wartość zostanie zapisana indywidualnie przy tym pracowniku.";

  useEffect(() => {
    const currentPositionId = employeePosition.position?.id ?? null;
    if (previousPositionId.current !== null && currentPositionId !== previousPositionId.current) {
      onChange({
        rateSource: "",
        rateBillingType: BILLING_TYPE.HOURLY,
        hourlyRate: "",
        netHourlyRate: "",
        monthlyRate: "",
        netMonthlyRate: ""
      });
    }
    previousPositionId.current = currentPositionId;
  }, [employeePosition.position?.id]);

  useEffect(() => {
    const completedTrainingIds = new Set(
      (employee.employeeTrainings || [])
        .filter(item => item.completed && item.training?.id)
        .map(item => item.training.id)
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const validCertificationIds = new Set(
      (employee.employeeCertifications || [])
        .filter(item => {
          if (!item.certificationType?.id) return false;
          if (item.noExpirationDate) return true;
          if (!item.validTo) return false;
          const validFrom = item.validFrom ? new Date(item.validFrom) : null;
          const validTo = new Date(item.validTo);
          if (validFrom) validFrom.setHours(0, 0, 0, 0);
          validTo.setHours(0, 0, 0, 0);
          return (!validFrom || validFrom <= today) && validTo >= today;
        })
        .map(item => item.certificationType.id)
    );

    if (employeePosition?.position) {
      setMissingTrainingsNote(
        (employeePosition.position.requiredTrainings || [])
          .filter(item => !completedTrainingIds.has(item.id))
          .map(item => item.name)
          .join(', ')
      );

      setMissingCertificationsNote(
        (employeePosition.position.requiredCertifications || [])
          .filter(item => !validCertificationIds.has(item.id))
          .map(item => item.name)
          .join(', ')
      );
    } else {
      setMissingTrainingsNote(null);
      setMissingCertificationsNote(null);
    }
  }, [employeePosition?.position, employee.employeeTrainings, employee.employeeCertifications]);

  const handleRateSourceChange = (event) => {
    const source = event.target.value;

    if (source === RATE_SOURCE.CONTRACT && contractAvailability.available) {
      onChange({
        rateSource: RATE_SOURCE.CONTRACT,
        rateBillingType: contractAvailability.billingType,
        hourlyRate: contractAvailability.billingType === BILLING_TYPE.HOURLY ? contractAvailability.grossRate : "",
        netHourlyRate: contractAvailability.billingType === BILLING_TYPE.HOURLY ? (contractAvailability.netRate ?? "") : "",
        monthlyRate: contractAvailability.billingType === BILLING_TYPE.MONTHLY ? contractAvailability.grossRate : "",
        netMonthlyRate: contractAvailability.billingType === BILLING_TYPE.MONTHLY ? (contractAvailability.netRate ?? "") : ""
      });
      return;
    }

    if (source === RATE_SOURCE.POSITION && positionAvailability.available) {
      onChange({
        rateSource: RATE_SOURCE.POSITION,
        rateBillingType: BILLING_TYPE.HOURLY,
        hourlyRate: positionAvailability.grossRate,
        netHourlyRate: positionAvailability.netRate ?? "",
        monthlyRate: "",
        netMonthlyRate: ""
      });
      return;
    }

    if (source === RATE_SOURCE.INDIVIDUAL) {
      onChange({
        rateSource: RATE_SOURCE.INDIVIDUAL,
        rateBillingType: billingType || BILLING_TYPE.HOURLY
      });
    }
  };

  const handleBillingTypeChange = (event) => {
    const value = event.target.value;
    onChange({
      rateBillingType: value,
      hourlyRate: value === BILLING_TYPE.HOURLY ? employeePosition.hourlyRate : "",
      netHourlyRate: value === BILLING_TYPE.HOURLY ? employeePosition.netHourlyRate : "",
      monthlyRate: value === BILLING_TYPE.MONTHLY ? employeePosition.monthlyRate : "",
      netMonthlyRate: value === BILLING_TYPE.MONTHLY ? employeePosition.netMonthlyRate : ""
    });
  };

  const updateEvaluation = () => {
    onChange({evaluationDate: new Date().toISOString().split("T")[0]});
  };

  const handleRemoveClick = () => {
    if (employeePosition.position?.name) setShowModal(true);
    else onRemove();
  };

  return (
    <div className="employee-position-item">
      <div className="employee-position-main-grid">
        <div className="employee-position-field employee-position-field-position">
          <label>Stanowisko</label>
          <SelectNested
            propertyName={`employeePositions[${index}].position`}
            state={state}
            itemsList={positionsList}
            displayNames={["name"]}
            showInput={false}
            showDelete={false}
            useId={false}
            disabled={employeePosition.id > 0}
          />
          {position?.department?.name && <small>Dział: {position.department.name}</small>}
        </div>

        <div className="employee-position-field employee-position-field-source">
          <label>Źródło stawki</label>
          <select className="form-select" value={selectedRateSource} onChange={handleRateSourceChange}>
            <option value="" disabled>Wybierz źródło stawki</option>
            <option
              value={RATE_SOURCE.CONTRACT}
              disabled={!contractAvailability.available && selectedRateSource !== RATE_SOURCE.CONTRACT}
            >
              {contractAvailability.available
                ? `Umowa — ${contractAvailability.label}`
                : `Umowa — niedostępna (${contractAvailability.reason})`}
            </option>
            <option
              value={RATE_SOURCE.POSITION}
              disabled={!positionAvailability.available && selectedRateSource !== RATE_SOURCE.POSITION}
            >
              {positionAvailability.available
                ? `Definicja stanowiska — ${positionAvailability.label}`
                : `Definicja stanowiska — niedostępna (${positionAvailability.reason})`}
            </option>
            <option value={RATE_SOURCE.INDIVIDUAL}>Stawka indywidualna</option>
          </select>
          <small>{sourceDescription}</small>
        </div>

        <div className="employee-position-field employee-position-field-rate">
          <div className="employee-position-rate-heading">
            <label>Stawka przypisana pracownikowi</label>
            {selectedRateSource && (
              <span className="employee-position-rate-badge">
                {billingType === BILLING_TYPE.MONTHLY ? "miesięczna" : "godzinowa"}
              </span>
            )}
          </div>

          {selectedRateSource === RATE_SOURCE.INDIVIDUAL ? (
            <>
              <div className="employee-position-rate-controls">
                <select className="form-select" value={billingType} onChange={handleBillingTypeChange}>
                  <option value={BILLING_TYPE.HOURLY}>Godzinowo</option>
                  <option value={BILLING_TYPE.MONTHLY}>Miesięcznie</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={storedGrossRate ?? ""}
                  onChange={(e) => onChange({
                    [billingType === BILLING_TYPE.MONTHLY ? "monthlyRate" : "hourlyRate"]: e.target.value
                  })}
                  placeholder={billingType === BILLING_TYPE.MONTHLY ? "Brutto / mies." : "Brutto / h"}
                  className="form-control"
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={storedNetRate ?? ""}
                  onChange={(e) => onChange({
                    [billingType === BILLING_TYPE.MONTHLY ? "netMonthlyRate" : "netHourlyRate"]: e.target.value
                  })}
                  placeholder={billingType === BILLING_TYPE.MONTHLY ? "Netto / mies." : "Netto / h"}
                  className="form-control"
                />
              </div>
            </>
          ) : selectedRateSource ? (
            <div className="employee-position-rate-readonly">
              <strong>
                {money(storedGrossRate) || "—"} zł/{billingType === BILLING_TYPE.MONTHLY ? "mies." : "h"} brutto
              </strong>
              {money(storedNetRate) && <span>{money(storedNetRate)} zł netto</span>}
            </div>
          ) : (
            <div className="employee-position-rate-empty">Wybierz źródło, aby przypisać stawkę.</div>
          )}

          <button
            type="button"
            className="btn btn-sm btn-outline-primary employee-position-period-rates"
            disabled={!state.currentItem?.id || !employeePosition.position?.id || !employeePosition.id || employeePosition.id <= 0}
            title={employeePosition.id > 0
              ? "Okresowe stawki indywidualne mają pierwszeństwo przed stawką bazową przypisaną tutaj."
              : "Najpierw zapisz przypisanie stanowiska"}
            onClick={() => setShowRatesModal(true)}
          >
            Stawki i koszt
          </button>
        </div>

        <div className="employee-position-field employee-position-field-evaluation">
          <label>Ocena</label>
          <input
            type="number"
            id={`position_evaluation-${employeePosition.id}`}
            value={evaluation}
            onChange={(e) => onChange({evaluation: e.target.value})}
            min={settings.positionEvaluationMinimumValue}
            max={settings.positionEvaluationMaximumValue}
            className="form-control"
          />
        </div>

        <div className="employee-position-field employee-position-field-active">
          <label>Aktywne</label>
          <div className="employee-position-toggle-wrap">
            <Toggler propertyName={`employeePositions[${index}].active`} state={state}/>
          </div>
        </div>

        <div className="employee-position-actions">
          <button type="button" className="btn btn-outline-danger" onClick={handleRemoveClick} title="Usuń stanowisko">
            <FontAwesomeIcon icon={faTrashAlt}/>
          </button>
        </div>
      </div>

      <div className="employee-position-secondary-grid">
        <div className="employee-position-field employee-position-field-comments">
          <label>Uwagi</label>
          <input
            type="text"
            value={employeePosition.comments || ""}
            onChange={(e) => onChange({comments: e.target.value})}
            placeholder="Uwagi do przypisania stanowiska"
            className="form-control"
          />
        </div>

        <div className="employee-position-field employee-position-field-date">
          <label>Data oceny</label>
          <div className="employee-position-date-control">
            <input type="text" value={evaluationDate} className="form-control" disabled/>
            <button
              type="button"
              className="btn btn-primary"
              onClick={updateEvaluation}
              disabled={employeePosition.id < 0}
              title="Ustaw dzisiejszą datę oceny"
            >
              <FontAwesomeIcon icon={faArrowsRotate}/>
            </button>
          </div>
        </div>

        <div className="employee-position-requirement">
          <label>Wymagane szkolenia</label>
          {missingTrainingsNote
            ? <div className="employee-position-requirement-status missing">{missingTrainingsNote}</div>
            : <div className="employee-position-requirement-status ok">Brak</div>}
        </div>

        <div className="employee-position-requirement">
          <label>Wymagane certyfikaty</label>
          {missingCertificationsNote
            ? <div className="employee-position-requirement-status missing">{missingCertificationsNote}</div>
            : <div className="employee-position-requirement-status ok">Brak</div>}
        </div>
      </div>

      <EmployeePositionRatesModal
        show={showRatesModal}
        onHide={() => setShowRatesModal(false)}
        employeeId={state.currentItem?.id}
        employeeName={`${state.currentItem?.firstName || ""} ${state.currentItem?.lastName || ""}`.trim()}
        positionId={employeePosition.position?.id}
        positionName={employeePosition.position?.name}
        legacyRate={employeePosition.hourlyRate}
        legacyNetRate={employeePosition.netHourlyRate}
        legacyBillingType={employeePosition.rateBillingType}
        legacyMonthlyRate={employeePosition.monthlyRate}
        legacyNetMonthlyRate={employeePosition.netMonthlyRate}
      />

      {showModal && (
        <ElementRemovalWarningModal
          onRemove={() => { setShowModal(false); onRemove(); }}
          onCancel={() => setShowModal(false)}
          warningText={<>Czy na pewno usunąć stanowisko <strong>{employeePosition.position?.name}</strong>?</>}
        />
      )}
    </div>
  );
};

export default EmployeePosition;

